import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { TopBar } from "@/components/TopBar";
import { Footer } from "@/components/Footer";
import { CreateRoomCard, JoinRoomCard } from "@/components/dashboard/RoomCards";
import { SupportInbox } from "@/components/dashboard/SupportInbox";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { TranscriptButtons } from "@/components/room/TranscriptButtons";
import { NoTraceTag } from "@/components/room/Privacy";
import { fmtDate, langName } from "@/lib/langs";

const RecentRooms = ({ rooms }) => (
  <div className="holo-panel" data-testid="recent-rooms">
    <div className="flex items-center justify-between border-b border-border px-5 py-4">
      <span className="eyebrow">[03] Sessions</span>
      <span className="mono-label">{rooms.length} total</span>
    </div>
    {rooms.length === 0 && <p className="p-5 font-mono text-xs text-muted-foreground">No rooms yet. Create one to begin.</p>}
    <ul>
      {rooms.slice(0, 8).map((r) => (
        <li key={r.room_id} className="flex items-center justify-between gap-4 border-b border-border px-5 py-3 last:border-0" data-testid={`recent-room-${r.room_id}`}>
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <span className="font-mono text-sm">{r.room_id}</span>
              {r.encrypted && <NoTraceTag />}
              <span className={`font-mono text-[10px] tracking-widest ${r.status === "active" ? "text-[color:var(--au-green)]" : "text-muted-foreground"}`}>
                {r.status === "active" ? "● LIVE" : "○ CLOSED"}
              </span>
            </div>
            <div className="mt-1 truncate text-xs text-muted-foreground">{[...new Set(r.participants.map((p) => p.language.toUpperCase()))].join(" · ")} · {r.participants.length}/4 people · {fmtDate(r.created_at)}</div>
          </div>
          {r.status === "active" ? (
            <Link to={`/room/${r.room_id}`} className="icon-btn shrink-0" data-testid={`recent-room-open-${r.room_id}`}><ArrowUpRight size={16} /></Link>
          ) : r.encrypted ? (
            <span className="shrink-0 font-mono text-[10px] tracking-widest text-muted-foreground" data-testid={`recent-room-purged-${r.room_id}`}>DELETED</span>
          ) : (
            <TranscriptButtons roomId={r.room_id} compact />
          )}
        </li>
      ))}
    </ul>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    api.get("/rooms/mine").then((r) => setRooms(r.data)).catch(() => {});
    api.get("/me/usage").then((r) => setUsage(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen">
      <TopBar />
      <main className="mx-auto max-w-[1440px] px-4 py-10 sm:px-8 lg:py-16">
        <div className="fade-up flex flex-col justify-between gap-6 border-b border-border pb-10 md:flex-row md:items-end">
          <div>
            <div className="eyebrow">Operator // {user?.email}</div>
            <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tighter sm:text-5xl lg:text-6xl">Hello, {user?.name?.split(" ")[0]}.</h1>
          </div>
          <div className="flex gap-10 font-mono" data-testid="dashboard-usage">
            <div><div className="text-3xl font-semibold tabular-nums">{usage?.total_minutes ?? "—"}</div><div className="mono-label mt-1">Minutes</div></div>
            <div><div className="text-3xl font-semibold tabular-nums">{usage?.sessions ?? "—"}</div><div className="mono-label mt-1">Sessions</div></div>
            <div><div className="text-3xl font-semibold tabular-nums">{rooms.length}</div><div className="mono-label mt-1">Rooms</div></div>
          </div>
        </div>
        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="space-y-8 lg:col-span-7">
            <CreateRoomCard />
            <JoinRoomCard />
            <RecentRooms rooms={rooms} />
          </div>
          <div className="lg:col-span-5"><SupportInbox /></div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
